import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { IndustryOnboardingData, ScreenType, IndustryType } from '../types'
import { STORAGE_KEYS } from '../lib/utils'

interface OnboardingStore {
  // Current state
  currentScreen: ScreenType
  data: IndustryOnboardingData
  
  // Autosave state
  lastSaved: Date | string | null
  isSaving: boolean
  hasUnsavedChanges: boolean
  saveError: boolean
  
  // Navigation
  setCurrentScreen: (screen: ScreenType) => void
  nextScreen: () => void
  previousScreen: () => void
  canProceed: () => boolean
  
  // Data management
  updateData: <K extends keyof IndustryOnboardingData>(key: K, value: IndustryOnboardingData[K]) => void
  updatePartialData: (partial: Partial<IndustryOnboardingData>) => void
  resetData: () => void
  
  // Autosave methods
  triggerAutosave: () => Promise<void>
  markAsUnsaved: () => void
  markAsSaved: () => void
  
  // Industry-specific helpers
  setSelectedIndustry: (industry: IndustryType) => void
  getProgress: () => number
  
  // Validation
  validateCurrentScreen: () => { isValid: boolean; errors: string[] }
  
  // File handling
  
  // Smart defaults
  applySmartDefaults: () => void
}

const initialData: IndustryOnboardingData = {
  selectedIndustry: '',
  businessProfile: {
    businessType: '',
    annualRevenue: '',
    companySize: '',
    location: {
      city: '',
      state: '',
      zipCode: '',
      serviceRadius: ''
    }
  },
  contactInfo: {
    companyName: '',
    contactPerson: '',
    email: '',
    phone: ''
  },
  revenueStreams: [],
  suppliers: [],
  competitiveProfile: {
    serviceArea: {
      city: '',
      state: '',
      zipCode: '',
      radius: ''
    },
    knownCompetitors: [],
    competitiveAdvantages: []
  },
  // uploadedFiles removed in Phase 1
  deliveryPreferences: {
    frequency: 'monthly', // Smart default
    format: ['email-pdf', 'web-dashboard'], // Smart defaults - most popular options
    alertThreshold: 'major-only', // Smart default - prevents alert fatigue
    priorities: [
      'revenue-changes',
      'supply-chain', 
      'competitive-activity'
    ], // Smart defaults - top 3 high-priority areas
    deliveryEmail: '' // Will be set when user enters email
  },
  paymentInfo: undefined, // Will be populated after payment processing
  orchestratorSubmitted: false, // Track if profile sent to orchestrator
  additionalNotes: ''
}

const screenOrder: ScreenType[] = [
  'industry-selection',
  'business-profile',
  'revenue-intelligence',
  'supply-chain-intelligence',
  'competitive-intelligence',
  'service-selection', // Added in Phase 2
  'intelligence-delivery',
  'payment-activation', // Added in Phase 4
  'intelligence-summary' 
]

export const useOnboardingStore = create<OnboardingStore>()(
  persist(
    (set, get) => ({
      currentScreen: 'industry-selection',
      data: initialData,
      lastSaved: null,
      isSaving: false,
      hasUnsavedChanges: false,
      saveError: false,
      
      setCurrentScreen: (screen) => set({ currentScreen: screen }),
      
      nextScreen: () => {
        const { currentScreen, triggerAutosave } = get()
        const currentIndex = screenOrder.indexOf(currentScreen)
        if (currentIndex < screenOrder.length - 1) {
          // Trigger autosave before moving to next screen
          triggerAutosave()
          set({ currentScreen: screenOrder[currentIndex + 1] })
        }
      },
      
      previousScreen: () => {
        const { currentScreen, triggerAutosave } = get()
        const currentIndex = screenOrder.indexOf(currentScreen)
        if (currentIndex > 0) {
          // Trigger autosave before moving to previous screen
          triggerAutosave()
          set({ currentScreen: screenOrder[currentIndex - 1] })
        }
      },
      
      canProceed: () => {
        const { validateCurrentScreen } = get()
        return validateCurrentScreen().isValid
      },
      
      updateData: (key, value) => {
        set((state) => ({
          data: { ...state.data, [key]: value },
          hasUnsavedChanges: true
        }))
      },
      
      updatePartialData: (partial) => {
        set((state) => ({
          data: { ...state.data, ...partial },
          hasUnsavedChanges: true
        }))
      },
      
      resetData: () => {
        set({ 
          data: initialData, 
          currentScreen: 'industry-selection',
          lastSaved: null,
          hasUnsavedChanges: false,
          saveError: false
        })
      },
      
      triggerAutosave: async () => {
        const { data, hasUnsavedChanges } = get()
        
        if (!hasUnsavedChanges) return
        
        set({ isSaving: true, saveError: false })
        
        try {
          // Simulate API call to save data
          await new Promise(resolve => setTimeout(resolve, 1000))
          
          // In production, this would be an actual API call:
          // await api.saveOnboardingProgress(data)
          
          // For now, data is already persisted to localStorage via zustand persist
          const now = new Date()
          set({ 
            lastSaved: now.toISOString(), // Store as ISO string for proper serialization
            isSaving: false,
            hasUnsavedChanges: false,
            saveError: false
          })
        } catch (error) {
          console.error('Autosave failed:', error)
          set({ 
            isSaving: false,
            saveError: true
          })
        }
      },
      
      markAsUnsaved: () => {
        set({ hasUnsavedChanges: true })
      },
      
      markAsSaved: () => {
        const now = new Date()
        set({ 
          hasUnsavedChanges: false,
          lastSaved: now.toISOString() // Store as ISO string
        })
      },
      
      setSelectedIndustry: (industry) => {
        set((state) => ({
          data: { ...state.data, selectedIndustry: industry },
          hasUnsavedChanges: true
        }))
      },
      
      getProgress: () => {
        const { currentScreen } = get()
        const currentIndex = screenOrder.indexOf(currentScreen)
        return Math.round(((currentIndex + 1) / screenOrder.length) * 100)
      },
      
      applySmartDefaults: () => {
        set((state) => ({
          data: {
            ...state.data,
            deliveryPreferences: {
              ...state.data.deliveryPreferences,
              frequency: state.data.deliveryPreferences.frequency || 'monthly',
              format: state.data.deliveryPreferences.format.length > 0 
                ? state.data.deliveryPreferences.format 
                : ['email-pdf', 'web-dashboard'],
              alertThreshold: state.data.deliveryPreferences.alertThreshold || 'major-only',
              priorities: state.data.deliveryPreferences.priorities.length > 0 
                ? state.data.deliveryPreferences.priorities 
                : ['revenue-changes', 'supply-chain', 'competitive-activity']
            }
          },
          hasUnsavedChanges: true
        }))
      },
      
      validateCurrentScreen: () => {
        const { currentScreen, data } = get()
        const errors: string[] = []
        
        switch (currentScreen) {
          case 'industry-selection':
            if (!data.selectedIndustry) {
              errors.push('Please select an industry')
            }
            break
            
          case 'business-profile':
            if (!data.contactInfo.companyName) {
              errors.push('Company name is required')
            }
            if (!data.contactInfo.contactPerson) {
              errors.push('Contact person is required')
            }
            if (!data.contactInfo.email) {
              errors.push('Email address is required')
            }
            if (!data.businessProfile.businessType) {
              errors.push('Business type is required')
            }
            break
            
          case 'revenue-intelligence':
            // Made optional for better UX
            break
            
          case 'supply-chain-intelligence':
            // Made optional for better UX
            break
            
          case 'competitive-intelligence':
            // Made optional to allow users to proceed to service selection
            // Service selection (Step 6) is the critical step that shouldn't be skipped
            break
            
          case 'service-selection':
            if (!data.selectedService?.id) {
              errors.push('Please select a service package')
            }
            break
            
          case 'intelligence-delivery':
            // Email is required for delivery
            if (!data.contactInfo.email) {
              errors.push('Email address is required for delivery')
            }
            break
            
          case 'payment-activation':
            const selectedService = data.selectedService
            const isPaidService = selectedService && selectedService.price > 0
            const isFreeService = selectedService && selectedService.price === 0
            
            if (isPaidService) {
              // For paid services, require payment completion
              if (!data.paymentInfo?.paymentStatus || data.paymentInfo.paymentStatus !== 'succeeded') {
                errors.push('Payment must be completed to proceed')
              }
            } else if (isFreeService) {
              // For free services, require activation (same validation as paid services)
              if (!data.paymentInfo?.paymentStatus || data.paymentInfo.paymentStatus !== 'succeeded') {
                errors.push('Click Activate Free Service to continue')
              }
            }
            break            
        }
        
        return {
          isValid: errors.length === 0,
          errors
        }
      },
      
      addUploadedFile: async (file) => {
        // Convert file to base64 for storage
        const reader = new FileReader()
        return new Promise((resolve) => {
          reader.onload = () => {
            const uploadedFile = {
              name: file.name,
              type: file.type,
              size: file.size,
              lastModified: file.lastModified,
              data: reader.result as string
            }
            
            set((state) => ({
              data: {
                ...state.data,
                uploadedFiles: [...state.data.uploadedFiles, uploadedFile]
              },
              hasUnsavedChanges: true
            }))
            
            resolve()
          }
          reader.readAsDataURL(file)
        })
      },
      
      removeUploadedFile: (fileName) => {
        set((state) => ({
          data: {
            ...state.data,
            uploadedFiles: state.data.uploadedFiles.filter(f => f.name !== fileName)
          },
          hasUnsavedChanges: true
        }))
      }
    }),
    {
      name: STORAGE_KEYS.ONBOARDING_DATA,
      partialize: (state) => ({
        currentScreen: state.currentScreen,
        data: state.data,
        lastSaved: state.lastSaved // This will be stored as string in localStorage
      })
    }
  )
)